import { Components } from "@thisgorilla/primate-ds";
import moment from "moment";
import { createContext, useRef, useState } from "react";
import styled, { useTheme } from "styled-components";

import { addProxyUrlToProfileImage } from "../../../../../utils";
import {
  FlexColumn,
  FlexRow,
  FlexRowCenter,
  FlexRowSpaced,
} from "../../../Styles";
import { CommentProps } from "../Comment";
import { CommentOptions } from "./CommentOptions";
import { ReportComment } from "./ReportComment";
import { API_PATHS } from "../../../../../API/api_paths";
import AnonymousUserToolTip from "../../../AnonymousUserToolTip";
import { useAppSelector } from "../../../../../redux";

export const CommentHeaderContext = createContext({} as CommentProps.Context);
export const CommentHeader = ({
  comment_user,
  created_at,
  post_id,
  id,
  user_id,
  parent_comment_id,
  setIsEditComment,
}: CommentProps.CommentHeader) => {
  const { typography, Color } = useTheme();
  const { anonymous_persona } = useAppSelector((state) => state.me);
  const commentOptionsRef = useRef<HTMLDivElement>(null);
  const { first_name, last_name, profile_picture, user_location } =
    comment_user;
  const [showCommentOptions, setShowCommentOptions] = useState(false);
  const [reportCommentOpen, setReportCommentOpen] = useState(false);

  const name = first_name + " " + last_name;
  const isAnonymousUser = name === anonymous_persona;
  return (
    <CommentHeaderContext.Provider
      value={{
        commentOptionsRef,
        setShowCommentOptions,
        post_id,
        comment_id: id,
        comment_user_name: name,
        user_id: user_id,
        setReportCommentOpen,
        parent_comment_id,
        setIsEditComment,
      }}
    >
      <Container>
        <FlexRow $gapInPx={10}>
          <Components.Avatar
            size="Small-32"
            src={
              profile_picture.startsWith("anon")
                ? API_PATHS.global.anonymous_persona + profile_picture
                : addProxyUrlToProfileImage(profile_picture)
            }
            type="Circle"
            alt="profile-picture"
          />
          <FlexColumn $gapInPx={0}>
            <FlexRow>
              <FlexRowCenter>
                <Components.Text
                  style={{ tokenStyle: typography.Body.L }}
                  text={`${first_name} ${last_name}`}
                  fontWeight="font-weight-bold"
                />
                {isAnonymousUser && (
                  <Components.Tooltip
                    type="component"
                    label={<AnonymousUserToolTip />}
                    showTriangle={false}
                  >
                    <Components.Icon
                      type="Info"
                      size="16px"
                      color={Color.icon["icon-tertiary"].value}
                    />
                  </Components.Tooltip>
                )}
              </FlexRowCenter>
            </FlexRow>
            <Components.Text
              text={user_location}
              style={{ tokenStyle: typography.Body.S }}
              color={Color.text["text-tertiary"].value}
            />
          </FlexColumn>
        </FlexRow>
        <FlexRow $gapInPx={4}>
          <PostedDateAndIcon $gapInPx={2}>
            <Components.Text
              text={`${moment(created_at).fromNow(true)} •`}
              style={{ tokenStyle: typography.Body.S }}
            />
            <Components.Icon type="Globe" size="16px" />
          </PostedDateAndIcon>
          <IconButtonContainer>
            <Components.IconButton
              disabled={false}
              iconType="MoreHorizontal"
              kind="Tertiary"
              size="Small"
              onClick={() => setShowCommentOptions((prev) => !prev)}
            />
            {showCommentOptions && <CommentOptions />}
          </IconButtonContainer>
        </FlexRow>
      </Container>
      {reportCommentOpen && (
        <ReportComment
          setReportCommentOpen={setReportCommentOpen}
          modalOpen={reportCommentOpen}
        />
      )}
    </CommentHeaderContext.Provider>
  );
};

const Container = styled(FlexRowSpaced)`
  align-items: self-start;
  padding: ${({ theme }) => theme.spacing["spacing-2"].value}rem
    ${({ theme }) => theme.spacing["spacing-none"].value}rem;

  .tootltip-children {
    display: flex;
    align-items: center;
  }
`;
const IconButtonContainer = styled.div`
  position: relative;
`;

const PostedDateAndIcon = styled(FlexRow)`
  align-items: center;
`;
